import { useEffect, useState } from 'react';
import { Alert, Box, ButtonBase, Stack, Typography } from '@mui/material';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import SearchIcon from '@mui/icons-material/Search';
import SlideshowOutlinedIcon from '@mui/icons-material/SlideshowOutlined';
import { api } from '../../lib/api';
import { navigate, paths } from '../../lib/router';
import { useAuthStore } from '../../store/authStore';
import { useDepartmentName } from '../../store/departmentStore';
import { DocumentRow } from './DocumentsPage';
import { DeckCard, DeckGridSkeleton, EmptyState, PageHeader, Section } from './portalParts';
import { deckGrid, timeAgo } from './portalStyles';

type Results = Awaited<ReturnType<typeof api.search>>;

/** Results for the header search box: decks you can open, live announcements and documents. */
export function SearchPage({ q }: { q: string }) {
  const myEmail = useAuthStore((s) => s.user?.email?.toLowerCase() ?? '');
  const departmentName = useDepartmentName();
  const [results, setResults] = useState<Results | null>(null);
  const [error, setError] = useState<string | null>(null);
  const query = q.trim();

  useEffect(() => {
    if (query.length < 2) return;
    api
      .search(query)
      .then(setResults)
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Search failed');
        setResults({ decks: [], announcements: [], documents: [] });
      });
  }, [query]);

  if (query.length < 2) {
    return (
      <>
        <PageHeader title="Search" icon={<SearchIcon color="primary" />} />
        <EmptyState>Type at least two characters in the search box above.</EmptyState>
      </>
    );
  }

  const total = results ? results.decks.length + results.announcements.length + results.documents.length : 0;

  return (
    <>
      <PageHeader
        title={`Results for “${query}”`}
        description={results ? `${total} ${total === 1 ? 'match' : 'matches'}` : 'Searching…'}
        icon={<SearchIcon color="primary" />}
      />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {results === null ? (
        <DeckGridSkeleton count={4} />
      ) : total === 0 ? (
        <EmptyState>Nothing matches “{query}”. Try a different word.</EmptyState>
      ) : (
        <>
          {results.decks.length > 0 && (
            <Section title="Decks" icon={<SlideshowOutlinedIcon />}>
              <Box sx={deckGrid}>
                {results.decks.map((d) => (
                  <DeckCard
                    key={d.id}
                    deck={d}
                    onOpen={() =>
                      navigate(d.owner_email.toLowerCase() === myEmail ? paths.edit(d.id) : paths.view(d.id))
                    }
                  />
                ))}
              </Box>
            </Section>
          )}
          {results.announcements.length > 0 && (
            <Section title="Announcements" icon={<CampaignOutlinedIcon />}>
              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2.5, bgcolor: 'background.paper', overflow: 'hidden' }}>
                {results.announcements.map((a) => (
                  <ButtonBase
                    key={a.id}
                    onClick={() => navigate(paths.announcements())}
                    sx={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      px: 2,
                      py: 1.5,
                      borderTop: 1,
                      borderColor: 'divider',
                      '&:first-of-type': { borderTop: 0 },
                      '&:hover': { bgcolor: 'chrome.subtle' },
                    }}
                  >
                    <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                      {a.title}
                    </Typography>
                    <Typography variant="caption" component="div" noWrap>
                      {a.body}
                    </Typography>
                    <Typography variant="caption" component="div" color="text.secondary">
                      {departmentName(a.category)} · {timeAgo(a.created_at)}
                    </Typography>
                  </ButtonBase>
                ))}
              </Box>
            </Section>
          )}
          {results.documents.length > 0 && (
            <Section title="Documents" icon={<DescriptionOutlinedIcon />}>
              <Stack sx={{ border: 1, borderColor: 'divider', borderRadius: 2.5, bgcolor: 'background.paper', overflow: 'hidden' }}>
                {results.documents.map((doc) => (
                  <DocumentRow key={doc.id} doc={doc} />
                ))}
              </Stack>
            </Section>
          )}
        </>
      )}
    </>
  );
}
